import { useEffect, useState } from "react";
import ReactTooltip from "@tippyjs/react";
import { AlertTriangle, CheckCircle2, FileText, MessageSquarePlus, RotateCcw } from "lucide-react";
import { Editor, EditorProvider, Toolbar, BtnBold, BtnItalic, BtnBulletList } from "react-simple-wysiwyg";
import CopyIcon from "components/icons/copy-icon";
import NsLoader from "components/ui/ns-loader";
import PanelTabs from "components/ui/panel-tabs";
import { cn } from "components/ui/cn";

/* The right-hand box: one tab per selected template, one note on show.

   This replaced a stack of cards, which in turn replaced an earlier tab row.
   Tabs are what the owner asked for, and they also retire a real hazard: the
   stacked version could only put copy / read-aloud / regenerate on the *focused*
   card, because those handlers read `active` from their closure and would have
   acted on the wrong note anywhere else. With one note visible, the note on
   screen is always `active`, so the actions can live permanently in the footer.

   The footer mirrors the transcript box's own bottom controls — same position,
   same weight — so the two panels stay one object seen twice. */

const BLOCK_COPY = {
  short_session:
    "Session too short to write up. Give it at least 30 seconds.",
  short_transcript:
    "Not enough transcript to write up yet. Keep going and try again.",
};

const NotesPanel = ({
  templates,
  generatedNotes,
  active,
  setActive,
  isEditingNote,
  setIsEditingNote,
  setEditorVal,
  setIsNoteEdited,
  htmlValue,
  notesRef,
  loading,
  notesBlockReason,
  retryingTemplateId,
  isRecording,
  isCopied,
  copyToClipboard,
  // handleDictate is intentionally not rendered — the owner does not want a
  // read-aloud control on the notes box. The handler stays wired so the feature
  // can be restored without re-threading it through save-session.jsx.
  handleDictate,
  handleRetryTemplate,
  TypewriterMarkdown,
  typedNotesRef,
  addMoreSlot,
  refineSlot,
  onReorderTemplates,
  onRemoveTemplate,
  className,
}) => {
  const [isRefineOpen, setIsRefineOpen] = useState(false);
  const noteFor = (templateId) =>
    (generatedNotes || []).find((n) => parseInt(n.templateId, 10) === templateId)?.output || "";

  const activeTemplate = templates.find((t) => t.id === active);

  // Never leave the box showing nothing because `active` points at a template
  // that is no longer selected — removing the open one used to blank the panel.
  useEffect(() => {
    if (templates.length && !templates.some((t) => t.id === active)) {
      setActive(templates[0].id);
    }
  }, [templates, active, setActive]);

  useEffect(() => {
    if (!refineSlot) setIsRefineOpen(false);
  }, [refineSlot]);

  const isRetryingActive = activeTemplate && retryingTemplateId === activeTemplate.id;
  const activeNote = activeTemplate ? noteFor(activeTemplate.id) : "";
  const canAct = !!activeTemplate && !loading && !notesBlockReason;

  const actionButton =
    "flex items-center rounded-ns-sm p-1.5 text-muted transition-colors duration-ui ease-ui hover:bg-accent-tint hover:text-accent active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-40";

  const renderBody = () => {
    if (notesBlockReason) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4 px-4 text-center">
          <AlertTriangle size={28} className="text-warn" />
          <p className="max-w-xs font-body text-ui-sm leading-relaxed text-muted">
            {BLOCK_COPY[notesBlockReason] ?? BLOCK_COPY.short_transcript}
          </p>
        </div>
      );
    }

    if (loading) {
      // size="sm" to match the status line under the Start listening button —
      // the default md set the label two steps larger than anything near it.
      return (
        <div className="flex min-h-[64px] items-center">
          <NsLoader size="sm" />
        </div>
      );
    }

    if (!activeTemplate) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-tint">
            <FileText size={18} className="text-accent" />
          </span>
          <p className="font-body text-ui-sm font-medium text-ink">Your notes land here.</p>
          <p className="max-w-[15rem] font-body text-ui-xs leading-relaxed text-muted">
            Add a template and Nirva writes the consult up against it when you stop.
          </p>
        </div>
      );
    }

    if (isRetryingActive) {
      return <NsLoader size="sm" dots={false} bare label="Writing it again" />;
    }

    if (isEditingNote) {
      return (
        <div ref={notesRef} className="nirva-notes-editor text-sm">
          <EditorProvider>
            <Editor
              value={htmlValue}
              disabled={isRecording}
              onChange={(e) => {
                setIsNoteEdited(true);
                setEditorVal(e.target.value);
              }}
            >
              <Toolbar>
                <BtnBold />
                <BtnItalic />
                <BtnBulletList />
              </Toolbar>
            </Editor>
          </EditorProvider>
        </div>
      );
    }

    if (!activeNote) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-tint">
            <FileText size={18} className="text-accent" />
          </span>
          <p className="font-body text-ui-sm font-medium text-ink">
            Your {activeTemplate.name} lands here.
          </p>
          <p className="max-w-[16rem] font-body text-ui-xs leading-relaxed text-muted">
            Record the consult, then stop — the write-up starts on its own.
          </p>
        </div>
      );
    }

    return (
      <div
        onClick={() => {
          if (isRecording || loading) return;
          setIsEditingNote(true);
          setEditorVal(activeNote);
        }}
        // text-ink, not the inherited muted: this is the clinical note itself,
        // the one thing on the screen a clinician actually reads and signs off.
        className="nirva-notes-editor cursor-text text-sm text-ink"
      >
        <TypewriterMarkdown
          speed={2}
          startDelay={400}
          uniqueId={String(activeTemplate.id)}
          typedNotesRef={typedNotesRef}
          text={activeNote}
        />
      </div>
    );
  };

  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      {/* Title, hairline, tabs, then Add more hard right. h-12 is shared with the
          Session header in save-session.jsx so the two rules, and therefore the
          two boxes below them, line up. Change one and you must change the other. */}
      <div className="flex h-12 flex-shrink-0 items-stretch gap-3 border-b border-solid border-rule">
        <div className="flex flex-shrink-0 items-center">
          <h2 className="font-display text-ui-md font-normal text-ink">Notes</h2>
        </div>

        {templates.length > 0 && <div className="my-3 w-px flex-shrink-0 bg-rule-strong" />}

        <PanelTabs
          tabs={templates.map((t) => ({ id: t.id, label: t.name }))}
          activeId={active}
          onSelect={(id) => {
            setActive(id);
            setIsEditingNote(false);
          }}
          onReorder={onReorderTemplates}
          onRemove={onRemoveTemplate}
          removeLabel="Remove"
          ariaLabel="Templates"
          className="flex-1"
        />

        <div className="flex flex-shrink-0 items-center">{addMoreSlot}</div>
      </div>

      {/* The surface. Same radius, hairline and elevation as the transcript box
          (components/TranscriptContainer.jsx) — the two panels are one object
          seen twice, so they carry one set of surface styles. */}
      <div className="flex min-h-0 flex-1 flex-col pt-3">
        <div className="group flex min-h-0 flex-1 flex-col rounded-ns border border-solid border-rule bg-white shadow-card">
          {/* Keyed on the open tab so switching template fades the new note in
              rather than swapping it in place. */}
          <div key={active} className="transcript-scroll min-h-0 flex-1 animate-fade-up overflow-y-auto p-4">
            {renderBody()}
          </div>

          {refineSlot && isRefineOpen && (
            <div className="flex-shrink-0 border-t border-solid border-rule px-4 py-2">
              {refineSlot}
            </div>
          )}

          <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-solid border-rule px-4 py-1.5">
            <p className="truncate font-body text-meta italic text-muted">
              Have a read before you sign off.
            </p>

            <div className="flex flex-shrink-0 items-center gap-1">
              {refineSlot && (
                <ReactTooltip content={isRefineOpen ? "Close" : "Ask for a change"}>
                  <button
                    type="button"
                    onClick={() => setIsRefineOpen((open) => !open)}
                    aria-expanded={isRefineOpen}
                    className={cn(actionButton, isRefineOpen && "bg-accent-tint text-accent")}
                  >
                    <MessageSquarePlus size={16} />
                  </button>
                </ReactTooltip>
              )}

              <ReactTooltip content={isCopied ? "Copied." : "Copy note"}>
                <button type="button" onClick={copyToClipboard} disabled={!canAct} className={actionButton}>
                  {isCopied ? <CheckCircle2 size={16} className="text-accent" /> : <CopyIcon />}
                </button>
              </ReactTooltip>

              <ReactTooltip content="Write it again">
                <button
                  type="button"
                  onClick={handleRetryTemplate}
                  disabled={!canAct || isRetryingActive || isRecording}
                  className={actionButton}
                >
                  <RotateCcw size={16} />
                </button>
              </ReactTooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotesPanel;
